import { ContentBlock, ContentBlockType, ContentPage, ListType } from '../../types/content';

type ChapterSource = {
  id: string;
  file: string;
  readTime: string;
};

const chapters: ChapterSource[] = [
  { id: 'scale-from-zero-to-millions', file: 'scale-from-zero-to-millions.md', readTime: '60–90 min read' },
  { id: 'back-of-the-envelope-estimation', file: 'back-of-the-envelope-estimation.md', readTime: '45–60 min read' },
  { id: 'system-design-interview-framework', file: 'system-design-interview-framework.md', readTime: '45–60 min read' },
];

const clean = (value: string) => value
  .replace(/\*\*/g, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(new RegExp(String.fromCharCode(96), 'g'), '')
  .trim();

const tableRow = (line: string) => line
  .trim()
  .replace(/^\||\|$/g, '')
  .split('|')
  .map((cell) => clean(cell.trim()));

export const markdownToContentBlocks = (markdown: string): { title: string; blocks: ContentBlock[] } => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: ContentBlock[] = [];
  const fence = String.fromCharCode(96).repeat(3);
  let title = 'HLD Fundamentals';
  let order = 0;
  let index = 0;

  const add = (block: Omit<ContentBlock, 'order'>) => blocks.push({ ...block, order: order++ });
  const isTableSeparator = (line: string) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
  const isBlockStart = (line: string) => /^#{1,6}\s|^[-*+]\s+|^\d+\.\s+|^>\s?|^\|/.test(line) || line.trimStart().startsWith(fence);

  while (index < lines.length) {
    const line = lines[index].trimEnd();
    if (!line.trim() || /^---+$/.test(line.trim())) {
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const content = clean(heading[2]);
      if (level === 1) title = content;
      else add({ type: ContentBlockType.HEADING, level: level === 2 ? 4 : level === 3 ? 5 : 6, content });
      index += 1;
      continue;
    }

    if (line.startsWith(fence)) {
      const language = line.slice(fence.length).trim() || 'text';
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trimStart().startsWith(fence)) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      add({ type: ContentBlockType.CODE, codeConfig: { language, code: codeLines.join('\n'), showLineNumbers: false } });
      continue;
    }

    if (line.trimStart().startsWith('>')) {
      const quoteLines: string[] = [];
      while (index < lines.length && lines[index].trimStart().startsWith('>')) {
        quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }
      add({ type: ContentBlockType.QUOTE, content: clean(quoteLines.join(' ')) });
      continue;
    }

    if (line.trimStart().startsWith('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const headers = tableRow(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].trimStart().startsWith('|')) {
        rows.push(tableRow(lines[index]));
        index += 1;
      }
      add({ type: ContentBlockType.TABLE, tableConfig: { headers, rows, striped: true } });
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.+)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (bullet || numbered) {
      const listType = numbered ? ListType.ORDERED : ListType.UNORDERED;
      const items: string[] = [];
      while (index < lines.length) {
        const candidate = listType === ListType.ORDERED
          ? lines[index].match(/^\s*\d+\.\s+(.+)$/)
          : lines[index].match(/^\s*[-*+]\s+(.+)$/);
        if (!candidate) break;
        items.push(clean(candidate[1]));
        index += 1;
      }
      add({ type: ContentBlockType.LIST, listType, listItems: items });
      continue;
    }

    const paragraph: string[] = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    add({ type: ContentBlockType.PARAGRAPH, content: clean(paragraph.join(' ')) });
  }

  return { title, blocks };
};

export const loadHLDFundamentalsContent = async (): Promise<Record<string, ContentPage>> => {
  const loaded = await Promise.all(chapters.map(async (chapter) => {
    const response = await fetch('/hld-content/' + chapter.file);
    if (!response.ok) throw new Error('Unable to load ' + chapter.file);
    const parsed = markdownToContentBlocks(await response.text());
    const slug = 'hld-' + chapter.id;
    return [slug, {
      id: slug,
      slug,
      title: parsed.title,
      description: parsed.blocks.find((block) => block.type === ContentBlockType.QUOTE)?.content,
      blocks: parsed.blocks,
      category: 'system-design',
      subcategory: 'hld',
      tags: ['hld'],
      author: { name: 'Sachin', fullName: 'Sachin Saini' },
      lastUpdated: 'October 2026',
      readTime: chapter.readTime,
      status: 'published',
      version: 1,
      viewCount: 0,
      likeCount: 0,
      averageRating: 0,
      ratingCount: 0,
      isActive: true,
    } satisfies ContentPage] as const;
  }));

  return Object.fromEntries(loaded);
};
